// /portal/support/:id — client-side detail view of a single support ticket
// (Phase D). Symmetric counterpart to admin SupportDetail, with two key
// inversions:
//   1. Status-transition / claim affordances are not shown — clients never
//      transition or own tickets server-side.
//   2. Message highlighting is inverted from admin — the client's own
//      messages get the accent border + soft background, mirroring
//      Messages.tsx UX where "you" is the highlighted side.
//
// All write surface is gated on `status !== 'resolved'` (server returns 409
// otherwise; UI hides the input to spare the round trip).

import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  SUPPORT_CATEGORY_LABEL,
  getStatusLabel,
  type SupportMessageDto,
  type SupportTicketDto,
} from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';

// Mirrors AdminLayout's ROLE_LABELS — kept inline so this single-use
// constant doesn't justify a new shared file. See Support.tsx for the
// same constant; if a third portal file ever needs these labels, hoist
// to src/client/lib/role-labels.ts.
const ROLE_LABELS: Record<string, string> = {
  super: 'Super Admin', admin: 'Admin', lead: 'Engagement Lead',
  analyst: 'Analyst', compliance: 'Compliance', finance: 'Finance', readonly: 'Read-only',
};

function formatStamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 16).replace('T', ' ');
}

const REPLY_MAX = 5000;

export function PortalSupportDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [ticket, setTicket] = useState<SupportTicketDto | null>(null);
  const [messages, setMessages] = useState<SupportMessageDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initial parallel fetch — ticket + thread. Same pattern as admin
  // SupportDetail; client scope is enforced server-side (403/404 if not
  // your ticket).
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([
      apiGet<{ ticket: SupportTicketDto }>(`/api/support/${id}`),
      apiGet<{ messages: SupportMessageDto[] }>(`/api/support/${id}/messages`),
    ])
      .then(([t, m]) => {
        if (cancelled) return;
        setTicket(t.ticket);
        setMessages(m.messages);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.serverMessage : 'Could not load ticket.');
      });
    return () => { cancelled = true; };
  }, [id]);

  // Keep scroll pinned to bottom whenever the thread grows.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  async function handleSendReply() {
    if (!ticket || sending) return;
    const body = reply.trim();
    if (body.length < 1 || body.length > REPLY_MAX) return;
    if (ticket.status === 'resolved') return;
    setSending(true);
    setReplyError(null);
    try {
      const r = await apiPost<{ message: SupportMessageDto; ticket: SupportTicketDto }>(
        `/api/support/${ticket.id}/messages`,
        { body },
      );
      setMessages((prev) => [...(prev ?? []), r.message]);
      // Returned ticket reflects any auto-flip (awaiting_client → in_progress).
      setTicket(r.ticket);
      setReply('');
    } catch (err) {
      setReplyError(err instanceof ApiError ? err.serverMessage : 'Could not send reply.');
    } finally {
      setSending(false);
    }
  }

  if (loadError) {
    return (
      <>
        <h1 className="portal-h1">Ticket not found</h1>
        <p className="portal-sub">{loadError}</p>
        <Link className="btn ghost" to="/portal/support" style={{ marginTop: 24, display: 'inline-block' }}>
          ← Support
        </Link>
      </>
    );
  }
  if (!ticket || messages === null) return <p className="portal-sub">Loading…</p>;

  const isResolved = ticket.status === 'resolved';
  const isAwaitingClient = ticket.status === 'awaiting_client';
  const ownerLine = ticket.owner_user_id && ticket.owner_name
    ? `Handled by ${ticket.owner_name}${ticket.owner_role ? `, ${ROLE_LABELS[ticket.owner_role] ?? ticket.owner_role}` : ''}.`
    : 'Awaiting triage.';
  const replyDisabled = isResolved || sending || reply.trim().length < 1 || reply.length > REPLY_MAX;

  // Chip tones — keep aligned with portal Support.tsx + admin pages.
  const statusTone: Record<string, string> = {
    open:            'amber',
    acknowledged:    'accent',
    in_progress:     'accent',
    awaiting_client: 'amber',
    resolved:        'green',
    closed:          'neutral',
  };

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--fg-mute)', marginBottom: 8 }}>
        <Link to="/portal/support" style={{ borderBottom: '1px solid var(--rule)' }}>← Support</Link>
        {' / '}<span className="rid">{ticket.id}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="portal-h1">{ticket.subject}</h1>
          <p className="portal-sub">
            <span className="rid">{ticket.id}</span> · {SUPPORT_CATEGORY_LABEL[ticket.category]}
            {ticket.related_ref && <> · ref <span className="rid">{ticket.related_ref}</span></>}
          </p>
        </div>
        <span className={`chip ${statusTone[ticket.status]}`}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
          {getStatusLabel(ticket.status, 'client')}
        </span>
      </div>

      {/* Meta strip */}
      <div className="card" style={{ marginTop: 16, padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, fontSize: 13 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--fg-mute)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
              Opened
            </div>
            <div style={{ color: 'var(--fg)' }}>{formatStamp(ticket.created_at)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--fg-mute)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
              Owner
            </div>
            <div style={{ color: ticket.owner_user_id ? 'var(--fg)' : 'var(--fg-mute)' }}>{ownerLine}</div>
          </div>
          {ticket.resolved_at && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--fg-mute)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
                Resolved
              </div>
              <div style={{ color: 'var(--fg)' }}>{formatStamp(ticket.resolved_at)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Status banners */}
      {isAwaitingClient && (
        <div
          className="card"
          style={{
            marginTop: 14,
            padding: 12,
            borderLeft: '3px solid var(--amber, #d4a017)',
            background: 'var(--bg-soft)',
            fontSize: 13.5,
            color: 'var(--fg)',
          }}
        >
          Omega is waiting for your reply.
        </div>
      )}
      {isResolved && (
        <div
          className="card"
          style={{
            marginTop: 14,
            padding: 12,
            borderLeft: '3px solid var(--green)',
            background: 'var(--bg-soft)',
            fontSize: 13.5,
            color: 'var(--fg)',
          }}
        >
          Resolved on {formatStamp(ticket.resolved_at)}. Open a new ticket if needed.
        </div>
      )}

      {/* Original message */}
      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ fontSize: 13, color: 'var(--fg-mute)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px' }}>
          Your original message
        </h3>
        <div
          style={{
            background: 'var(--bg-soft)',
            padding: 14,
            borderRadius: 4,
            fontSize: 13.5,
            lineHeight: 1.6,
            color: 'var(--fg-soft)',
            whiteSpace: 'pre-wrap',
            borderLeft: '3px solid var(--rule)',
          }}
        >
          {ticket.body}
        </div>
      </div>

      {/* Thread */}
      <div className="card" style={{ marginTop: 16, display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ fontSize: 13, color: 'var(--fg-mute)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px' }}>
          Conversation
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
          {messages.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--fg-mute)', textAlign: 'center', padding: 24 }}>
              No replies yet.
            </p>
          )}
          {messages.map((m) => {
            // Portal-side inversion: the client (= "you") is highlighted,
            // staff replies are neutral. Mirrors Messages.tsx where the
            // user's own messages carry the accent. Admin does the opposite.
            const isYou = m.author_kind === 'client' && m.author_id === user?.id;
            const accent = isYou;
            return (
              <div
                key={m.id}
                style={{
                  padding: 12,
                  borderRadius: 4,
                  background: accent ? 'var(--bg-soft)' : 'transparent',
                  borderLeft: `3px solid ${accent ? 'var(--accent)' : 'var(--rule)'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 12.5 }}>
                    <strong>{isYou ? 'You' : m.author_name}</strong>
                    <span style={{ color: 'var(--fg-mute)', marginLeft: 8, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10 }}>
                      {m.author_kind === 'staff' ? 'Omega' : 'Client'}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                    {formatStamp(m.created_at)}
                  </span>
                </div>
                <div style={{ fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', color: 'var(--fg)' }}>
                  {m.body}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply box */}
        <div style={{ marginTop: 14, borderTop: '1px solid var(--rule)', paddingTop: 14 }}>
          <textarea
            rows={4}
            className="prop-textarea"
            placeholder={isResolved ? 'This ticket is resolved — open a new ticket if you need more help.' : 'Write a reply…'}
            value={reply}
            disabled={isResolved || sending}
            maxLength={REPLY_MAX}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                void handleSendReply();
              }
            }}
          />
          {replyError && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 4 }}>{replyError}</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
              {reply.length} / {REPLY_MAX}
            </span>
            <button
              type="button"
              className="btn accent"
              onClick={handleSendReply}
              disabled={replyDisabled}
            >
              {sending ? 'Sending…' : 'Send reply'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
